import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import FormatCurrency from 'components/Common/FormatCurrency'
import Group from 'components/utils/Group'
import Heading from 'LuxKit/Typography/Heading'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import LineFlightCreditIcon from 'LuxKit/Icons/line/LineFlightCreditIcon'
import LineShipIcon from 'LuxKit/Icons/line/LineShipIcon'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { getCruiseBundleHotelItemNames, getMinBundleFlightCredit } from 'lib/cruises/cruiseBundleFlightCredits'
import formatOfferInclusionDescription from 'lib/offer/formatOfferInclusionDescription'
import getInclusionMinStayText from 'lib/offer/getInclusionMinStayText'

const BundleInclusionsHeader = styled(Group)`
  align-items: center;
  padding: ${rem(24)} 0;
`

const BundleSectionContent = styled(VerticalSpacer)`
  padding: 0 ${rem(4)};
`

const BundleInclusionList = styled(VerticalSpacer)`
  padding: ${rem(4)} ${rem(8)} 0;
`

const BundleSections = styled(VerticalSpacer)`
  width: 100%;
  border-top: 1px solid ${(p) => p.theme.palette.neutral.surface.lightForeground};
`

const BundleSection = styled(VerticalSpacer)`
  padding: ${rem(8)} ${rem(4)};
  border-bottom: 1px solid ${(p) => p.theme.palette.neutral.surface.lightForeground};
`

interface Props {
  bundle: App.CruiseOfferBundle
  standardInclusions: Array<App.PackageInclusion>
}

function CruiseBundleInclusions({ bundle, standardInclusions }: Props) {
  const intl = useIntl()
  const hasStandardInclusions = standardInclusions.length > 0
  const hotelBundleItemNames = getCruiseBundleHotelItemNames(bundle)
  const bundleMinFlightCredit = getMinBundleFlightCredit(bundle)
  const hasBundleFlightCredits = bundleMinFlightCredit > 0

  return <VerticalSpacer data-testid="cruise-offer-bundle-inclusions" gap={0}>
    <BundleInclusionsHeader direction="horizontal" gap={8}>
      <Heading as="h2" variant="heading6">
        {intl.formatMessage({
          id: 'm0UKI3jcpydxsMR_EGVY',
          defaultMessage: 'What\u2019s included',
          description: 'Section title for included items on experience gift print page',
        })}
      </Heading>
    </BundleInclusionsHeader>

    <BundleSections gap={0}>
      <BundleSection gap={hasStandardInclusions ? 4 : 0}>
        <BodyTextBlock
          variant="medium"
          weight="bold"
          startIcon={<LineShipIcon colour="highlight-primary-dark" size="S" />}
        >
          {intl.formatMessage({
            id: 'kOwmAbePYrVdCY1Tl2t_',
            defaultMessage: 'Cruise',
            description: 'Cruise product type label in cruise order info tile',
          })}
        </BodyTextBlock>

        {hasStandardInclusions && <BundleSectionContent gap={4}>
          <BundleInclusionList gap={4}>
            {standardInclusions.map((inclusion) => {
              const description = formatOfferInclusionDescription(inclusion.description, intl)
              const minStayDescription = getInclusionMinStayText(inclusion, intl)

              return <BodyTextBlock
                key={inclusion.id}
                variant="medium"
                weight={inclusion.isHighlighted ? 'bold' : 'normal'}
                colour={inclusion.isHighlighted ? 'highlight-primary-dark' : undefined}
              >
                {description} {minStayDescription}
              </BodyTextBlock>
            })}
          </BundleInclusionList>
        </BundleSectionContent>}
      </BundleSection>

      {hotelBundleItemNames.length > 0 && <BundleSection gap={4}>
        <BodyTextBlock
          variant="medium"
          weight="bold"
          startIcon={<LineBedIcon colour="highlight-primary-dark" size="S" />}
        >
          {intl.formatMessage({
            id: 'CSBJ-jSuHiSUQsjPNnvn',
            defaultMessage: 'Hotel stays',
            description:
              'Section heading on cruise offer page for bundle inclusions that contain pre-cruise or post-cruise accommodation items.',
          })}
        </BodyTextBlock>

        <BundleSectionContent gap={4}>
          {hotelBundleItemNames.map((hotelBundleItemName, index) => <BodyTextBlock
            key={`${hotelBundleItemName}-${index}`}
            variant="medium"
          >
            {hotelBundleItemName}
          </BodyTextBlock>)}
        </BundleSectionContent>
      </BundleSection>}

      {hasBundleFlightCredits && <BundleSection gap={4}>
        <BodyTextBlock
          variant="medium"
          weight="bold"
          startIcon={<LineFlightCreditIcon colour="highlight-primary-dark" size="S" />}
        >
          {intl.formatMessage({
            id: 'DS90b9K6YjQWvevSSVG0',
            defaultMessage: 'Flight credits',
            description:
              'Section heading on cruise offer page for bundle inclusions that provide a flight credit amount for selected packages.',
          })}
        </BodyTextBlock>

        <BundleSectionContent gap={4}>
          <BodyTextBlock variant="medium">
            <FormattedMessage
              id="OQVewr2VDLwXciNq-Ii6"
              defaultMessage="from {amount} per person"
              description="Lowest flight credit value shown in cruise offer-page bundle inclusions, followed by a per-person qualifier."
              values={{ amount: <FormatCurrency value={bundleMinFlightCredit} /> }}
            />
          </BodyTextBlock>
          <BodyTextBlock variant="small">
            <FormattedMessage
              id="Yf9fZl2c1svVHOb5OK3w"
              defaultMessage="See fine print on how to redeem"
              description="Helper text in cruise offer bundle flight credits section directing customers to fine print redemption details."
            />
          </BodyTextBlock>
        </BundleSectionContent>
      </BundleSection>}
    </BundleSections>
  </VerticalSpacer>
}

export default CruiseBundleInclusions
